'use client';

import React from 'react';

export type FooterActionWeight = 'medium' | 'normal' | 'bold';

interface FooterActionButtonProps {
  /** Visible label — 'Clear' or 'Apply'. */
  label: string;
  /** Visual role — footer link tone for Clear, action tone for Apply. */
  variant: 'clear' | 'apply';
  /** Click handler wired straight through to the underlying button. */
  onClick: () => void;
  /** Font weight — defaults match each variant's existing style. */
  weight?: FooterActionWeight;
  /** Text size — hero search bar uses the body-size token, dropdown footers use 15px. */
  size?: 'body' | 'footer';
  /** Extra classes appended after the defaults (cannot remove the base style). */
  className?: string;
  /** Accessible label — defaults to the visible label. */
  ariaLabel?: string;
}

const WEIGHT_CLASSES: Record<FooterActionWeight, string> = {
  medium: 'font-medium',
  normal: 'font-normal',
  bold: 'font-bold',
};

const SIZE_CLASSES = {
  body: 'text-body-lg',
  footer: 'text-[15px]',
} as const;

const VARIANT_CLASSES = {
  clear: 'text-footer-link',
  apply: 'text-theme-action-primary',
} as const;

const DEFAULT_WEIGHTS: Record<'clear' | 'apply', FooterActionWeight> = {
  clear: 'medium',
  apply: 'bold',
};

/**
 * Atomic footer-action button for dropdown/panel footers.
 * Replaces the duplicated Clear/Apply `<button>` pairs across
 * filter dropdowns (hero search, blogs, specialities, shared ui).
 */
const FooterActionButton: React.FC<FooterActionButtonProps> = ({
  label,
  variant,
  onClick,
  weight,
  size = 'body',
  className = '',
  ariaLabel,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`cursor-pointer transition-opacity hover:opacity-70 ${VARIANT_CLASSES[variant]} ${SIZE_CLASSES[size]} ${WEIGHT_CLASSES[weight ?? DEFAULT_WEIGHTS[variant]]} ${className}`}
    aria-label={ariaLabel || label}
  >
    {label}
  </button>
);

export default FooterActionButton;
